// Web Push (VAPID) helpers.

import api from "@/lib/api";

export function isPushSupported() {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function isIos() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS legacy
    (typeof navigator !== "undefined" && navigator.standalone === true)
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

async function ensureRegistration() {
  // Zaregistruj SW pokud ještě není, a POČKEJ dokud není aktivní
  // (na iOS je to nutné, jinak subscribe() tichem selže).
  let reg = await navigator.serviceWorker.getRegistration();
  if (!reg) {
    reg = await navigator.serviceWorker.register("/service-worker.js");
  }
  // wait for active
  await navigator.serviceWorker.ready;
  reg = await navigator.serviceWorker.getRegistration();
  return reg;
}

export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;
  try {
    const reg = await ensureRegistration();
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Přihlásí zařízení a **uloží subscription na server**.
 * Vrací {ok, error?} — throws jen v případě fatal chyby.
 */
export async function subscribePush() {
  if (!isPushSupported()) {
    if (isIos() && !isStandalone()) {
      throw new Error(
        "Na iOS je nutné nejdřív aplikaci nainstalovat na plochu (Sdílet → Přidat na plochu) a otevřít odtud."
      );
    }
    throw new Error("Prohlížeč nepodporuje push notifikace.");
  }
  // iOS: musí být otevřeno z home screen jako standalone
  if (isIos() && !isStandalone()) {
    throw new Error(
      "Na iPhone musíš appku nejdřív přidat na plochu (Sdílet → Přidat na plochu) a spustit z ikony."
    );
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(
      permission === "denied"
        ? "Notifikace jsou zablokované v nastavení prohlížeče."
        : "Notifikace nebyly povoleny."
    );
  }

  const reg = await ensureRegistration();
  const keyRes = await api.get("/push/vapid-public-key");
  const publicKey = (keyRes.data?.public_key || "").trim();
  if (!publicKey) {
    throw new Error(
      "Server nemá nastavený VAPID public key. Kontaktuj správce — v .env chybí VAPID_PUBLIC_KEY / VAPID_PRIVATE_KEY_PEM / VAPID_SUBJECT."
    );
  }
  const applicationServerKey = urlBase64ToUint8Array(publicKey);

  // Pokud stávající subscription existuje, použij ji; jinak přihlas znovu.
  let sub = null;
  try {
    sub = await reg.pushManager.getSubscription();
  } catch {
    sub = null;
  }
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
    } catch (e) {
      throw new Error(
        `Přihlášení k push zprávám selhalo: ${e?.name || ""} ${e?.message || ""}`.trim()
      );
    }
  }
  if (!sub) {
    throw new Error("Prohlížeč nevrátil push subscription (neznámá chyba).");
  }

  const j = typeof sub.toJSON === "function" ? sub.toJSON() : {
    endpoint: sub.endpoint,
    keys: sub.keys || {},
  };
  if (!j?.endpoint || !j?.keys?.p256dh || !j?.keys?.auth) {
    // vyčisti nekompletní subscription a nahlas chybu
    try { await sub.unsubscribe(); } catch { /* ignore */ }
    throw new Error("Prohlížeč vrátil neúplnou subscription (chybí keys). Zkuste znovu.");
  }

  // Ulož na server; při chybě zpět odhlas browser
  try {
    await api.post("/push/subscribe", {
      endpoint: j.endpoint,
      keys: j.keys,
      user_agent: navigator.userAgent || "",
    });
  } catch (e) {
    try { await sub.unsubscribe(); } catch { /* ignore */ }
    const detail = e?.response?.data?.detail || e?.message || "Neznámá chyba";
    throw new Error(`Server odmítl subscription: ${detail}`);
  }
  return sub;
}

export async function unsubscribePush() {
  if (!isPushSupported()) return true;
  const reg = await ensureRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return true;
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch (e) {
    /* ignore */
  }
  try {
    await api.post("/push/unsubscribe", { endpoint });
  } catch { /* ignore */ }
  return true;
}

export async function testPush() {
  return await api.post("/push/test");
}

/** Vrátí server-side stav odběru pro tento endpoint (co má DB) */
export async function serverSubscriptionStatus() {
  const sub = await getCurrentSubscription();
  if (!sub) return { subscribed: false, endpoint: null };
  const r = await api.get("/push/status", { params: { endpoint: sub.endpoint } });
  return { ...r.data, endpoint: sub.endpoint };
}

/** Diagnostika pro debug (VAPID keys, subscription list) */
export async function pushDiagnostics() {
  const r = await api.get("/push/diagnostics");
  return r.data;
}
